// --- the reviewer's cascade, one row per judge call ------------------------
//
// A reviewed candidate is judged up to four times (contentgen-go-backend
// internal/pipeline/review.go, review_pair.go, review_video_prompt.go):
//
//   check   — the cheap tier. A FAIL is DROPPED when its confidence is below
//             the confidence gate or its severity below the severity gate;
//             otherwise it ESCALATES.
//   confirm — final for that attempt. A PASS overrules the check — unless a
//             REQUIRED finding failed, which overrides the pass and
//             regenerates. A FAIL regenerates, reconciles, rewires or drops.
//   pair    — the two-frame read (`frame: "pair"`).
//   keeps   — the text check guarding a video-prompt rewrite
//             (`frame: "video-prompt"`).
//
// Two further tiers are RECORDS rather than judgements of the candidate:
// `reconcile` (the confirm tier accepted a deviation and the engine rewrote the
// pending briefs) and `story-read` (the contact-sheet read).
//
// Only the NEWEST verdict has ever been shown, so a check that flagged
// something and was then overruled by a confirm pass left no trace in the UI.
// This module turns the append-only verdict log back into that sequence, and
// derives — from the verdicts, their metadata stamps and the profile's gates,
// and from nothing else — one line per row saying what the engine did next.
//
// Everything here is DERIVED. It never claims more than the record supports:
// with no gates known a dropped check says "not escalated" rather than
// inventing a number, and a reconcile the judge only ASKED for says nothing at
// all until the engine's own reconcile verdict proves it happened.

import { ReviewFinding, ReviewTrace, ReviewVerdict } from '../../../api/structs';
import { ReviewProfileGates } from '../../../api/structs/reviewProfile';
import { crossSceneLabel, readCrossScene } from './crossScene';

/** Tier ids in the words the reviewer is talked about in, not the wire's. */
export const TIER_LABELS: Record<string, string> = {
  check: 'Check',
  confirm: 'Confirm',
  pair: 'Frame pair',
  keeps: 'Video-prompt keeps',
  pick: 'Pick',
  reconcile: 'Brief reconcile',
  'story-read': 'Story read',
  'cast-bind': 'Cast binding',
  coherence: 'Prompt coherence',
};

const lower = (value: string | undefined): string => (value || '').trim().toLowerCase();

export const tierLabel = (tier: string): string => TIER_LABELS[lower(tier)] || tier || 'Check';

/**
 * The tiers that OPEN an attempt. Every attempt of the cascade begins with a
 * check (or, on a multi-candidate checkpoint, a pick); confirm, pair, keeps,
 * reconcile and story-read all hang off an attempt somebody else started.
 *
 * They are also the only tiers whose presence EVIDENCES a regeneration:
 * `recordReconcileVerdict` stamps `Attempt: len(verdicts) + 1` — a COUNT, not
 * an attempt — so a reconcile landing after `check#1, confirm#1` carries
 * attempt 3 and would otherwise read as "regenerated (attempt 3)" for a
 * candidate that was KEPT. `story-read` always carries attempt 1 and `keeps`
 * borrows the pair's; neither re-rolls an image either.
 */
const CASCADE_START_TIERS = new Set(['check', 'pick']);

export type ReviewStepOutcome = 'PASS' | 'FAIL' | 'UNCERTAIN' | 'UNREVIEWED';

export interface ReviewStep {
  /** Stable within one panel: the verdict's id, or its position. */
  key: string;
  verdict: ReviewVerdict;
  attempt: number;
  tier: string;
  label: string;
  /**
   * Which execution of the checkpoint this call belongs to, 1-based. Verdicts
   * are append-only ACROSS re-executions, and a user regenerate restarts at
   * attempt 1, so one log can hold several cascades and every derivation is
   * confined to one of them.
   */
  cascade: number;
  /** The model that answered, from the verdict or its trace. '' when unknown. */
  model: string;
  outcome: ReviewStepOutcome;
  findings: ReviewFinding[];
  critique: string;
  adjustedPrompt: string;
  /** The derived "what happened next" line; '' when nothing can be said. */
  next: string;
}

const failedOf = (verdict: ReviewVerdict): ReviewFinding[] =>
  (verdict.findings || []).filter((finding) => (finding.verdict || '').toUpperCase() === 'FAIL');

/**
 * Whether a REQUIRED criterion did not pass. The engine rules on these itself
 * (`evaluateRequiredChecks`): a required finding that is not a PASS fails the
 * attempt whatever the judge's overall verdict says — which is exactly the case
 * where "the confirm judge overruled the check" would be backwards.
 */
const requiredFailed = (verdict: ReviewVerdict): boolean =>
  (verdict.findings || []).some(
    (finding) => finding.required && (finding.verdict || '').toUpperCase() !== 'PASS'
  );

/** A verdict the reviewer could not give — an outage, never a ruling. */
export const isUnreviewed = (verdict: ReviewVerdict): boolean => verdict.outcome === 'unreviewed';

/**
 * A verdict's outcome as a pill. PASS/FAIL come straight off `pass`; UNCERTAIN
 * is the honest third answer for a judge that did not pass the call and named
 * no failing criterion — the engine treats it as a fail, but calling it one
 * would put a criterion count on a row that has none. UNREVIEWED is no answer
 * at all: the judge never ruled, so its `pass: false` says nothing.
 */
export const stepOutcome = (verdict: ReviewVerdict): ReviewStepOutcome => {
  if (isUnreviewed(verdict)) return 'UNREVIEWED';
  if (verdict.pass) return 'PASS';
  return failedOf(verdict).length > 0 ? 'FAIL' : 'UNCERTAIN';
};

/** The plan review rides the check tier, but it reads the whole plan. */
export const stepLabel = (verdict: ReviewVerdict): string =>
  (verdict.frame || '') === 'plan' ? 'Plan review' : tierLabel(verdict.tier);

/**
 * Which half of a two-frame scene a verdict belongs to. The scene-level tiers
 * (`pair`, `video-prompt`) sit with the opening frame — exactly where
 * SequencerStepRow already files them when it splits the two panels.
 */
export const verdictFrameGroup = (verdict: ReviewVerdict): 'opening' | 'closing' =>
  (verdict.frame || '') === 'closing' ? 'closing' : 'opening';

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/** `metadata.pair.regenerate`, only for the three words the engine accepts. */
const pairRegenerate = (verdict: ReviewVerdict): string => {
  const pair = asRecord((verdict.metadata || {}).pair);
  const value = typeof pair?.regenerate === 'string' ? pair.regenerate.trim().toLowerCase() : '';
  return value === 'opening' || value === 'closing' || value === 'both' ? value : '';
};

/** "the opening frame" / "the closing frame" / "both frames". */
const regenerateFrames = (value: string): string =>
  value === 'both' ? 'both frames' : `the ${value} frame`;

/**
 * A `video_prompt_adjustment_ignored` stamp is the bare refusal reason string
 * ("permission denied", "keeps check unavailable", …); tolerate an object with
 * a `reason` for anything a future engine writes.
 */
const ignoredReason = (value: unknown): string => {
  if (typeof value === 'string') return value.trim();
  const record = asRecord(value);
  return typeof record?.reason === 'string' ? record.reason.trim() : '';
};

/**
 * What the verdict's own stamps say the reviewer DID — the levers, as opposed
 * to the cascade's own next move. Every one of these is written by the engine
 * onto the verdict it acted on, so each is a fact rather than an inference.
 *
 * `metadata.reconcile` is deliberately NOT among them: it is what the confirm
 * judge ASKED for. The engine refuses it outright on a failing required
 * criterion or a withheld `AllowReconcile`, and `runReconcile` aborts
 * advisorily on eight further conditions — none of which leaves a
 * counter-stamp. The only proof a reconcile HAPPENED is the engine's own
 * reconcile-tier verdict, which `nextLine` looks for instead.
 */
const stampLines = (verdict: ReviewVerdict): string[] => {
  const metadata = verdict.metadata || {};
  const lines: string[] = [];
  if (metadata.drop_closing_frame_applied) lines.push('closing frame dropped');
  if (metadata.drop_closing_frame_ignored) {
    lines.push('closing-frame drop ignored (no permission)');
  }
  if (metadata.video_prompt_adjusted) lines.push('video prompt adjusted');
  if (metadata.video_prompt_adjustment_ignored) {
    const reason = ignoredReason(metadata.video_prompt_adjustment_ignored);
    lines.push(reason ? `adjustment ignored (${reason})` : 'adjustment ignored');
  }
  if (metadata.rewire_applied) lines.push('references rewired');

  // The two cross-scene stamps are a pair of opposites and only one is ever
  // written, so the line either reports the fix or reports that it was left to
  // the operator — never both. The scene comes off the stamp/proposal itself:
  // it is an EARLIER scene, not the one this verdict is about.
  const crossScene = readCrossScene(verdict);
  if (crossScene?.applied) {
    lines.push(`regenerated scene ${crossSceneLabel(crossScene.applied.sceneId)}`);
  } else if (crossScene?.suggested) {
    const scene = `suggested fixing scene ${crossSceneLabel(crossScene.sceneId)}`;
    lines.push(crossScene.suggestedReason ? `${scene} — ${crossScene.suggestedReason}` : scene);
  }
  return lines;
};

/**
 * Whether a failing check would have escalated, given the gates. Mirrors
 * `severityPassesGate` (review.go): the "any" gate escalates every failure,
 * anything else escalates only a blocking one.
 */
const severityPasses = (gate: string, severity: string): boolean =>
  gate.trim().toLowerCase() === 'any' || severity.trim().toLowerCase() === 'blocking';

/**
 * Why a failing check never reached the confirm judge, in the gate's own
 * numbers. Returns '' when the gates are unknown or when neither gate explains
 * it — the panel then says "not escalated" and claims nothing further.
 */
const dropReason = (verdict: ReviewVerdict, gates?: ReviewProfileGates | null): string => {
  if (!gates) return '';
  const confidenceGate = Number(gates.confidence);
  if (Number.isFinite(confidenceGate) && confidenceGate > 0 && verdict.confidence < confidenceGate) {
    return `dropped: confidence ${verdict.confidence} below the gate ${confidenceGate}`;
  }
  const severityGate = (gates.severity || '').trim();
  if (severityGate && verdict.severity && !severityPasses(severityGate, verdict.severity)) {
    return `dropped: severity ${verdict.severity} below the gate ${severityGate}`;
  }
  return '';
};

/**
 * The attempt a regeneration actually produced, or 0. Only a later
 * cascade-starting tier proves one: every other tier's `attempt` is either
 * borrowed or, for `reconcile`, a verdict COUNT the engine reuses as the field.
 */
const regeneratedAttempt = (later: ReviewVerdict[], verdict: ReviewVerdict): number => {
  const found = later.find(
    (candidate) =>
      CASCADE_START_TIERS.has(lower(candidate.tier)) &&
      (candidate.attempt || 0) > (verdict.attempt || 0) &&
      (candidate.frame || '') === (verdict.frame || '')
  );
  return found ? found.attempt : 0;
};

/** How many briefs a reconcile verdict says it rewrote (`metadata.overridden`). */
const reconciledBriefs = (verdict: ReviewVerdict): number => {
  const overridden = (verdict.metadata || {}).overridden;
  return Array.isArray(overridden) ? overridden.length : 0;
};

const UNREVIEWED_LINES: Record<string, string> = {
  config: 'reviewer not set up',
  credits: 'not enough credits for the reviewer',
};

/**
 * Why an outage row has no ruling. "(retried once)" only when the verdict's
 * own `judge_retry` stamp says a second call was made — a refused retry leaves
 * none, and the line must not claim one.
 */
const unreviewedLine = (verdict: ReviewVerdict): string => {
  const base = UNREVIEWED_LINES[verdict.failure_kind || ''] || 'reviewer unavailable';
  return (verdict.metadata || {}).judge_retry ? `${base} (retried once)` : base;
};

/**
 * The line under one row: what the engine did after this call. `cascade` is
 * this execution's whole sequence in chronological order, because half of these
 * answers can only be read off what came NEXT — and confining every lookup to
 * one cascade is what stops a LATER execution's attempt-1 confirm from being
 * read as the escalation of an earlier execution's dropped check.
 */
const nextLine = (
  cascade: ReviewVerdict[],
  index: number,
  gates?: ReviewProfileGates | null
): string => {
  const verdict = cascade[index];
  // An outage is not a failing check: no gate dropped it and no confirm judge
  // was asked, so the only true thing to say is why there is no ruling.
  if (isUnreviewed(verdict)) return unreviewedLine(verdict);
  const tier = lower(verdict.tier);
  const later = cascade.slice(index + 1);
  const earlier = cascade.slice(0, index);
  const parts: string[] = [];

  if (tier === 'check' && !verdict.pass) {
    const escalated = later.some(
      (candidate) =>
        lower(candidate.tier) === 'confirm' &&
        candidate.attempt === verdict.attempt &&
        (candidate.frame || '') === (verdict.frame || '')
    );
    if (escalated) parts.push('escalated to the confirm judge');
    else parts.push(dropReason(verdict, gates) || 'not escalated');
  }

  if (tier === 'confirm') {
    const attempt = regeneratedAttempt(later, verdict);
    const checkFailed = earlier.some(
      (candidate) =>
        lower(candidate.tier) === 'check' &&
        candidate.attempt === verdict.attempt &&
        !candidate.pass
    );

    if (verdict.pass && checkFailed) {
      // A required finding that is not a PASS OVERRIDES the confirm pass — the
      // engine regenerates anyway — so "overruled" would be exactly backwards.
      if (!requiredFailed(verdict)) parts.push('the confirm judge overruled the check');
      else if (attempt) {
        parts.push(`a required criterion still failed → regenerated (attempt ${attempt})`);
      } else parts.push('a required criterion still failed');
    }
    if (!verdict.pass && attempt) parts.push(`regenerated (attempt ${attempt})`);

    // Reported from the ENGINE's own record of the reconcile, never from the
    // judge's request: `metadata.reconcile` says only what was asked for.
    if ((verdict.metadata || {}).reconcile) {
      const applied = later.find((candidate) => lower(candidate.tier) === 'reconcile');
      if (applied) {
        const briefs = reconciledBriefs(applied);
        parts.push(briefs ? `brief reconciled (${briefs} rewritten)` : 'brief reconciled');
      }
    }
  }

  if (tier === 'pair' && !verdict.pass) {
    const regenerate = pairRegenerate(verdict);
    if (regenerate) parts.push(`regenerated ${regenerateFrames(regenerate)}`);
  }

  parts.push(...stampLines(verdict));
  return parts.join(' · ');
};

/**
 * Splits one frame's append-only verdict log into the CHECKPOINT EXECUTIONS it
 * records. A user regenerate re-runs the reviewer from attempt 1 and appends to
 * the same log, so "the confirm on my attempt" and "a later, higher attempt"
 * are only meaningful inside one execution.
 *
 * A cascade opens on a check (or pick) whose attempt is not greater than the
 * highest one this cascade has seen. Only those two tiers may open one:
 * `reconcile` carries a verdict COUNT in its attempt field and `story-read`
 * always carries 1, and either would otherwise split a cascade in half.
 */
export const segmentCascades = (verdicts: ReviewVerdict[]): ReviewVerdict[][] => {
  const cascades: ReviewVerdict[][] = [];
  let current: ReviewVerdict[] = [];
  let highest = 0;

  verdicts.forEach((verdict) => {
    if (CASCADE_START_TIERS.has(lower(verdict.tier))) {
      const attempt = verdict.attempt || 1;
      if (current.length > 0 && attempt <= highest) {
        cascades.push(current);
        current = [];
        highest = 0;
      }
      highest = Math.max(highest, attempt);
    }
    current.push(verdict);
  });

  if (current.length > 0) cascades.push(current);
  return cascades;
};

/**
 * The judge calls behind one panel, oldest FIRST, each with the line saying
 * what happened next.
 *
 * `verdicts` is the checkpoint's whole append-only log as the host handed it
 * over; only the ones judging the same frame half as `subject` (the verdict the
 * panel's badge speaks for) become rows, so a closing-frame panel never
 * narrates the opening frame's cascade.
 */
export const buildReviewSteps = (
  verdicts: ReviewVerdict[] | undefined,
  subject: ReviewVerdict,
  traces: ReviewTrace[],
  gates?: ReviewProfileGates | null
): ReviewStep[] => {
  const group = verdictFrameGroup(subject);
  const scoped = (verdicts || []).filter((verdict) => verdictFrameGroup(verdict) === group);
  const steps: ReviewStep[] = [];

  segmentCascades(scoped).forEach((cascade, cascadeIndex) => {
    cascade.forEach((verdict, index) => {
      // Same rule as the trace list's `tracesForVerdict`: the verdict id is
      // the exact link, the (attempt, tier, frame) coordinates are a genuine
      // FALLBACK rather than a legacy path — without which the `pair` and
      // `keeps` rows sat there model-less whenever an id went missing.
      const trace =
        (verdict.id && traces.find((candidate) => candidate.verdict_id === verdict.id)) ||
        traces.find(
          (candidate) =>
            candidate.attempt === verdict.attempt &&
            (candidate.tier || '') === (verdict.tier || '') &&
            (candidate.frame || '') === (verdict.frame || '')
        );
      steps.push({
        key: verdict.id || `${cascadeIndex}-${verdict.tier}-${verdict.attempt}-${index}`,
        verdict,
        attempt: verdict.attempt || 1,
        tier: verdict.tier || 'check',
        label: stepLabel(verdict),
        cascade: cascadeIndex + 1,
        model: verdict.model || trace?.model || '',
        outcome: stepOutcome(verdict),
        findings: verdict.findings || [],
        critique: (verdict.critique || '').trim(),
        adjustedPrompt: (verdict.adjusted_prompt || '').trim(),
        next: nextLine(cascade, index, gates),
      });
    });
  });

  return steps;
};
